/**
 * gd-cart-toast.js — non-blocking add-to-cart confirmation.
 * Claude 2026-08-07.
 *
 * WHY THIS EXISTS
 *   Until now every successful add dispatched `product-added-to-cart`, and
 *   gd-cart-drawer.js answered it with an unconditional open() — so a shopper
 *   working through the colour grid had to dismiss a full-screen drawer between
 *   every shade. The pre-rollout theme (147332104280) deliberately did NOT do
 *   this: both of its /cart/add.js paths have `// openCartDrawer();` commented
 *   out and call a small header popup instead. This restores that behaviour in
 *   the current design language.
 *
 * CONTRACT WITH THE REST OF THE CART STACK
 *   - `product-added-to-cart` is still dispatched, unchanged, for every add.
 *     gd-cart-sanitizer.js, gd-gift-with-purchase.js and gd-tier-gifts.js all
 *     listen on it. Nothing here touches that event.
 *   - The drawer still runs refresh() on every add; only open() is conditional.
 *   - FAIL-SAFE: gd-cart-add.js and gd-cart-drawer.js both check for
 *     `window.gdCartToast` before suppressing the drawer. If this file fails to
 *     load or bails early, the drawer opens exactly as it does today.
 *   - This file makes exactly one network call: a read-only GET /cart.js.
 *     It never mutates the cart and never touches a line property.
 *
 * SCOPE (data-scope, from the cart-drawer section settings)
 *   off                — never handle anything; drawer behaves as today
 *   quick_add          — collection-grid quick-adds only (default)
 *   quick_add_and_pdp  — grid quick-adds + product-page form adds
 *   Adds with no <form> in the event detail (the Salon Kit / Instant Gels
 *   builders, which build items[] and POST /cart/add.js themselves) are only
 *   ever claimed as FOLLOW-UPS to an add this component already owns — see
 *   handles() — so a builder add always gets the drawer.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-gd-cart-toast]');
  if (!root) return;

  var SCOPE = root.dataset.scope || 'quick_add';
  if (SCOPE === 'off') return; // never publish the global → nothing suppresses the drawer

  // Never on the cart page itself — the cart is already the whole screen there.
  if (document.body.classList.contains('template-cart')) return;

  var MONEY_FORMAT = root.dataset.moneyFormat || '';
  var GIFT_LABEL = root.dataset.giftLabel || '';
  var AUTO_DISMISS_MS = (parseInt(root.dataset.autoDismiss, 10) || 0) * 1000;

  // A formless dispatch may only ever be claimed on a page that actually has
  // quick-add tiles. Kit / bundle builder pages have none, so a builder add can
  // never be swallowed by this component regardless of timing.
  var PAGE_HAS_QUICK_ADD = !!document.querySelector('[data-gd-quick-add]');

  var el = {
    card:         root.querySelector('[data-gd-toast-card]'),
    image:        root.querySelector('[data-gd-toast-image]'),
    name:         root.querySelector('[data-gd-toast-name]'),
    variant:      root.querySelector('[data-gd-toast-variant]'),
    price:        root.querySelector('[data-gd-toast-price]'),
    progress:     root.querySelector('[data-gd-toast-progress]'),
    progressText: root.querySelector('[data-gd-toast-progress-text]'),
    progressFill: root.querySelector('[data-gd-toast-progress-fill]'),
    gift:         root.querySelector('[data-gd-toast-gift]'),
    giftText:     root.querySelector('[data-gd-toast-gift-text]'),
    count:        root.querySelector('[data-gd-toast-count]'),
    total:        root.querySelector('[data-gd-toast-total]'),
  };

  var COUNT_TPL = (el.count && el.count.dataset.template) || 'Cart ({count})';

  /* ── money ──────────────────────────────────────────────────────────────
     Formats from the shop's own money_format so the toast matches the rest of
     the storefront. Deliberately NOT reusing gd-cart-drawer.js's fmt(), which
     hardcodes the en-AU locale and renders "NOK 1,005.00" on the Nordic
     stores — a pre-existing cosmetic bug in the drawer, not one to copy. */
  function group(cents, decimals, thousandSep, decimalSep) {
    var n = (Number(cents) || 0) / 100;
    var parts = n.toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    return decimals > 0 && parts[1] ? whole + decimalSep + parts[1] : whole;
  }
  function formatMoney(cents) {
    if (!MONEY_FORMAT) return group(cents, 2, ',', '.');
    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case 'amount':                                  return group(cents, 2, ',', '.');
        case 'amount_no_decimals':                      return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator':             return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':        return group(cents, 2, "'", '.');
        case 'amount_with_space_separator':             return group(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(cents, 0, ' ', ',');
        default:                                        return group(cents, 2, ',', '.');
      }
    });
  }

  /* ── line-item predicates ───────────────────────────────────────────────
     Same rules as gd-cart-drawer.js:270-276 and the sanitizer's header block.
     A $0 line is NOT automatically a gift: kit/bundle children (_addon +
     __bundle_id) are $0 and are PAID FOR as part of the kit. Calling one of
     those a free gift misrepresents the bundle to the customer. */
  function props(it) { return (it && it.properties) || {}; }
  function isBundleLine(it) { return !!props(it).__bundle_id; }
  function isFreeGift(it) {
    var p = props(it);
    return !isBundleLine(it) && (p._free_gift === 'true' || !!p._gd_gift);
  }
  function cleanName(it) {
    var t = (it && (it.product_title || it.title)) || '';
    return t.split(' #')[0].trim() || t;
  }

  /* ── the added line ─────────────────────────────────────────────────── */
  function pickItem(detail) {
    var body = detail && detail.line;
    if (!body) return null;
    if (Array.isArray(body.items) && body.items.length) {
      // Prefer a real purchase over any $0 line the same POST may have carried.
      for (var i = 0; i < body.items.length; i++) {
        var p = body.items[i].final_price != null ? body.items[i].final_price : body.items[i].price;
        if (p > 0) return body.items[i];
      }
      return body.items[0];
    }
    return body.id || body.title ? body : null;
  }

  function sized(url, px) {
    if (!url) return '';
    return url.indexOf('?') > -1 ? url + '&width=' + px : url + '?width=' + px;
  }

  /* ── progress ────────────────────────────────────────────────────────
     Threshold and wording come from the drawer's own [data-gd-cart-progress]
     data attributes, so there is exactly one source of truth for the number and
     the localised sentence. Only the money substring is rendered here, via
     shop.money_format — mirroring the drawer's rendered HTML instead would drag
     in its en-AU fmt() and print "NOK 800.00" on a Norwegian storefront. */
  var progressSrc = document.querySelector('[data-gd-cart-progress]');

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderProgress(totalCents) {
    if (!el.progress) return;
    var threshold = progressSrc ? parseInt(progressSrc.dataset.threshold, 10) : 0;
    if (!threshold || threshold <= 0) { el.progress.hidden = true; return; }
    var label = '<span class="gd-cart__ship-bold">' + escapeHtml(progressSrc.dataset.freeShippingLabel || '') + '</span>';
    var html;
    if (totalCents >= threshold) {
      html = escapeHtml(progressSrc.dataset.progressUnlocked || '').replace('{label}', label);
    } else {
      var amt = '<span class="gd-cart__ship-amt">' + escapeHtml(formatMoney(threshold - totalCents)) + '</span>';
      html = escapeHtml(progressSrc.dataset.progressPending || '').replace('{amount}', amt).replace('{label}', label);
    }
    el.progressText.innerHTML = html;
    if (el.progressFill) el.progressFill.style.width = Math.min(100, Math.round((totalCents / threshold) * 100)) + '%';
    el.progress.hidden = false;
  }

  /* ── totals + gift state, straight from /cart.js ────────────────────── */
  var totalsAbort = null;
  function refreshTotals() {
    if (totalsAbort) { try { totalsAbort.abort(); } catch (e) {} }
    totalsAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: totalsAbort ? totalsAbort.signal : undefined })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        if (el.count) el.count.textContent = COUNT_TPL.replace('{count}', cart.item_count);
        if (el.total) el.total.textContent = formatMoney(cart.total_price);
        renderProgress(cart.total_price);
        if (el.gift && GIFT_LABEL) {
          var free = (cart.items || []).filter(isFreeGift);
          if (free.length) {
            el.giftText.textContent = GIFT_LABEL.replace('{title}', cleanName(free[0]));
            el.gift.hidden = false;
          } else {
            el.gift.hidden = true;
          }
        }
      })
      .catch(function () { /* totals are decoration — never block the toast */ });
  }

  /* ── position ────────────────────────────────────────────────────────
     Desktop: just under the sticky header. Mobile: above whatever fixed bar is
     already pinned to the bottom — on collection pages that is
     .gd-colours__filter-bar, the ONLY filter entry point below 990px. Measured
     at runtime rather than hardcoded so it adapts per template. */
  function place() {
    var header = document.querySelector('.gd-header') || document.querySelector('header');
    if (header) {
      var bottom = Math.round(header.getBoundingClientRect().bottom);
      if (bottom > 0) root.style.setProperty('--gd-toast-top', (bottom + 12) + 'px');
    }
    var bar = document.querySelector('.gd-colours__filter-bar');
    var clearance = 12;
    if (bar) {
      var br = bar.getBoundingClientRect();
      if (br.height > 0 && getComputedStyle(bar).display !== 'none' && Math.round(br.bottom) >= window.innerHeight - 2) {
        clearance = Math.round(br.height) + 12;
      }
    }
    root.style.setProperty('--gd-toast-bottom', clearance + 'px');
  }

  /* ── show / hide ────────────────────────────────────────────────────── */
  var dismissTimer = 0;
  var ownedUntil = 0;
  var paused = false;
  var lastFocus = null;
  // Must outlast the engines' own retry horizons, or a late reconcile falls
  // through to the drawer and pops it open with no shopper action:
  // gd-gift-with-purchase.js RETRY_WINDOW_MS = 15000, gd-tier-gifts.js BACKOFF_MS = 20000.
  var OWN_WINDOW_MS = 22000;
  function nowMs() { try { return Date.now(); } catch (e) { return 0; } }

  function show(detail) {
    // A dispatch carrying no <form> is one of the cart engines reconciling — the
    // gift-with-purchase or tier-gift reconciler re-dispatching
    // `product-added-to-cart` after it drops a free line in. Not a new shopper
    // action.
    var isFollowUp = !(detail && detail.form);

    // Once the card is off screen — dismissed by the shopper OR auto-hidden — a
    // later engine reconcile must stay silent: it refreshes the numbers, never
    // re-raises the card unbidden, and critically never falls through to the
    // drawer either.
    if (isFollowUp && root.hidden) { refreshTotals(); return; }

    if (!isFollowUp) {
      var item = pickItem(detail);
      if (item) {
        if (el.image) {
          el.image.src = sized(item.image || (item.featured_image && item.featured_image.url), 112);
          el.image.alt = cleanName(item);
        }
        if (el.name) el.name.textContent = cleanName(item);
        if (el.variant) {
          var v = item.variant_title;
          var showVariant = !!v && v !== 'Default Title';
          el.variant.textContent = showVariant ? v : '';
          el.variant.hidden = !showVariant;
        }
        if (el.price) el.price.textContent = formatMoney(item.final_price != null ? item.final_price : item.price);
      }
      // Clear the previous add's numbers so a repeat add can never show stale
      // totals for the duration of the /cart.js round-trip.
      if (el.count) el.count.textContent = '';
      if (el.total) el.total.textContent = '';
      if (el.progress) el.progress.hidden = true;
      if (el.gift) el.gift.hidden = true;
    }

    place();
    refreshTotals();

    ownedUntil = nowMs() + OWN_WINDOW_MS;
    if (root.hidden) lastFocus = document.activeElement;
    root.hidden = false;
    // restart the entry animation on a repeat add so it reads as a new event —
    // but not for an engine follow-up, which should not re-announce itself
    if (!isFollowUp && el.card) { el.card.style.animation = 'none'; void el.card.offsetWidth; el.card.style.animation = ''; }

    startDismissTimer();
  }

  // The countdown never runs while the shopper is actually reading the card:
  // hovering it or tabbing into it pauses, leaving restarts it. The old theme's
  // fixed 3s timer had no such pause, so it could vanish mid-read — which is the
  // whole reason a timed confirmation gets a bad name.
  function startDismissTimer() {
    if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = 0; }
    if (AUTO_DISMISS_MS > 0 && !paused) dismissTimer = setTimeout(hide, AUTO_DISMISS_MS);
  }

  function hide() {
    if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = 0; }
    var hadFocus = root.contains(document.activeElement);
    root.hidden = true;
    // NOTE: ownedUntil is deliberately NOT reset. The card going away — by tap or
    // by timer — must not hand a late gift reconcile the right to open the drawer.
    if (hadFocus && lastFocus && document.contains(lastFocus)) {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
    lastFocus = null;
  }

  /* ── policy: which adds does the toast own? ─────────────────────────── */
  function handles(detail) {
    // If the full cart is already open, leave it open and let it re-render.
    // A toast stacked on the drawer would be two confirmations of one action.
    var drawer = document.querySelector('[data-gd-cart-drawer]');
    if (drawer && !drawer.hidden) return false;

    var form = detail && detail.form;
    if (!form) {
      // Formless dispatch = a cart engine reconciling, not a shopper action.
      // Claim it only as a follow-up to an add this component already owned,
      // and only on a page that has quick-add tiles at all — so a Salon Kit or
      // Instant Gels builder add can never be swallowed here.
      return PAGE_HAS_QUICK_ADD && nowMs() < ownedUntil;
    }
    if (form.hasAttribute('data-gd-quick-add')) return true;   // collection-grid tiles
    return SCOPE === 'quick_add_and_pdp';                      // product-page forms, opt-in
  }

  /* ── wiring ─────────────────────────────────────────────────────────── */
  root.querySelectorAll('[data-gd-toast-close]').forEach(function (b) {
    b.addEventListener('click', hide);
  });
  root.querySelectorAll('[data-gd-toast-viewcart]').forEach(function (b) {
    b.addEventListener('click', function () {
      hide();
      document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !root.hidden) hide();
  });
  // pause the countdown while it is being read
  root.addEventListener('mouseenter', function () {
    paused = true;
    if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = 0; }
  });
  root.addEventListener('mouseleave', function () {
    paused = false;
    if (!root.hidden) startDismissTimer();
  });
  root.addEventListener('focusin', function () {
    paused = true;
    if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = 0; }
  });
  root.addEventListener('focusout', function (e) {
    if (root.contains(e.relatedTarget)) return;
    paused = false;
    if (!root.hidden) startDismissTimer();
  });
  // The gift engines mutate the cart a beat after the add — keep the numbers
  // honest while the toast is on screen.
  document.body.addEventListener('cart:updated', function () {
    if (root.hidden) return;
    refreshTotals();
  });
  // If the drawer is opened by any route, the toast is redundant.
  document.body.addEventListener('gd:cart-open', hide);
  window.addEventListener('resize', function () { if (!root.hidden) place(); });

  window.gdCartToast = { show: show, hide: hide, handles: handles, scope: SCOPE };
})();
